import { auth } from '../firebase';

export interface SseMessage {
  event?: string;
  data: string;
}

/**
 * Fetch-based SSE reader. Unlike `EventSource`, this supports POST,
 * custom headers, and request bodies — which we need for the authed
 * `/api/assistant/chat` endpoint.
 *
 * Yields one message per SSE frame. Terminates when the stream closes.
 */
export async function* parseSSE(
  path: string,
  init: { method?: 'GET' | 'POST'; body?: unknown } = {},
): AsyncGenerator<SseMessage> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();

  const API_BASE = import.meta.env.VITE_API_BASE as string;

  const res = await fetch(`${API_BASE}${path}`, {
    method: init.method ?? 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
  }
  if (!res.body) throw new Error('No response body');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by a blank line (\n\n).
      let sepIdx: number;
      while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, sepIdx);
        buffer = buffer.slice(sepIdx + 2);
        const msg = parseFrame(frame);
        if (msg) yield msg;
      }
    }
    // Flush any trailing frame.
    if (buffer.trim().length > 0) {
      const msg = parseFrame(buffer);
      if (msg) yield msg;
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* ignore */
    }
  }
}

function parseFrame(raw: string): SseMessage | null {
  const lines = raw.split('\n');
  let event: string | undefined;
  const dataLines: string[] = [];
  for (const line of lines) {
    if (!line || line.startsWith(':')) continue;
    const colon = line.indexOf(':');
    if (colon === -1) continue;
    const field = line.slice(0, colon);
    const value = line.slice(colon + 1).replace(/^ /, '');
    if (field === 'event') event = value;
    else if (field === 'data') dataLines.push(value);
  }
  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join('\n') };
}
